import { EditorEligibilityOperator } from '../editor-eligibility-operator/editor-eligibility-operator.model';
import { EditorEligibilityRule } from '../editor-eligibility-rule/editor-eligibility-rule.model';
import { EditorEligibilityOperatorWithRule } from '../editor-eligibility-operator-with-rule/editor-eligibility-operator-with-rule.interface';


export class EditorEligibilityTree {
  rootOperator: EditorEligibilityOperator;
  ruleList: EditorEligibilityRule[];

  constructor(obj?: any) {
    this.rootOperator = obj?.rootOperator ?? null;
    this.ruleList = obj?.ruleList ?? [];
  }

  immutableAddEmptyRuleAfter(operatorId: string): EditorEligibilityTree {
    return this.immutableAddRuleAfter(null, operatorId);
  }

  immutableAddRuleAfter(rule: EditorEligibilityRule | null, operatorId: string): EditorEligibilityTree {
    let rootOperatorCopy = this.rootOperator.createCompleteCopy();
    const operator = rootOperatorCopy.getOperatorById(operatorId);
    const newRuleIndex = operator.getLastHasRuleOperator().ruleIndex + 1;

    const newOperator = EditorEligibilityOperator.createDefault();
    newOperator.ancestorHideNestedDescriptions = operator.ancestorHideNestedDescriptions;
    newOperator.ruleIndex = newRuleIndex;

    if (operator.parent === null) {
      const newParentOperator = EditorEligibilityOperator.createDefault();
      newParentOperator.function = 'and';
      newParentOperator.children = [operator, newOperator];
      newOperator.parent = newParentOperator;
      operator.parent = newParentOperator;
      rootOperatorCopy = newParentOperator;
    } else {
      rootOperatorCopy.incrementRuleIndexFrom(newRuleIndex);

      newOperator.parent = operator.parent;

      const parentOperatorChildIndex = newOperator.parent.children.findIndex(child => child === operator);
      newOperator.parent.children = [
        ...newOperator.parent.children.slice(0, parentOperatorChildIndex + 1),
        newOperator,
        ...newOperator.parent.children.slice(parentOperatorChildIndex + 1)
      ];
    }

    return Object.assign(
      new EditorEligibilityTree(),
      this,
      {
        rootOperator: rootOperatorCopy,
        ruleList: [
          ...this.ruleList.slice(0, newRuleIndex),
          rule,
          ...this.ruleList.slice(newRuleIndex)
        ]
      }
    );
  }

  immutableMakeCompositeOperator(operatorId: string): EditorEligibilityTree {
    let rootOperatorCopy = this.rootOperator.createCompleteCopy();
    const operator = rootOperatorCopy.getOperatorById(operatorId);
    const newRuleIndex = operator.getLastHasRuleOperator().ruleIndex + 1;

    const newParentOperator = EditorEligibilityOperator.createDefault();
    newParentOperator.function = 'and';
    const newOperator = EditorEligibilityOperator.createDefault();
    newOperator.ruleIndex = newRuleIndex;

    newParentOperator.name = operator.name;
    newParentOperator.hideNestedDescriptions = operator.hideNestedDescriptions;
    newParentOperator.ancestorHideNestedDescriptions = operator.ancestorHideNestedDescriptions;
    operator.name = null;
    operator.hideNestedDescriptions = false;
    operator.ancestorHideNestedDescriptions = newParentOperator.ancestorHideNestedDescriptions || newParentOperator.hideNestedDescriptions;
    newOperator.ancestorHideNestedDescriptions = operator.ancestorHideNestedDescriptions;

    if (operator.parent === null) {
      rootOperatorCopy = newParentOperator;
    } else {
      rootOperatorCopy.incrementRuleIndexFrom(newRuleIndex);

      newParentOperator.parent = operator.parent;
      newParentOperator.parent.children[
        operator.parent.children.findIndex(child => child === operator)
      ] = newParentOperator;
    }

    newParentOperator.children = [operator, newOperator];
    operator.parent = newParentOperator;
    newOperator.parent = newParentOperator;

    return Object.assign(
      new EditorEligibilityTree(),
      this,
      {
        rootOperator: rootOperatorCopy,
        ruleList: [
          ...this.ruleList.slice(0, newRuleIndex),
          null,
          ...this.ruleList.slice(newRuleIndex)
        ]
      }
    );
  }

  immutableReplaceOperatorWithRule(
    operatorId: string,
    replacingOperatorWithRule: EditorEligibilityOperatorWithRule
  ): EditorEligibilityTree {
    let rootOperator;
    if (this.rootOperator.getOperatorById(operatorId) === replacingOperatorWithRule.operator) {
      rootOperator = this.rootOperator;
    } else {
      rootOperator = this.rootOperator.createCompleteCopy();
      const operator = rootOperator.getOperatorById(operatorId);

      replacingOperatorWithRule.operator.ancestorHideNestedDescriptions = operator.ancestorHideNestedDescriptions;
      replacingOperatorWithRule.operator.parent = operator.parent;
      replacingOperatorWithRule.operator.children = operator.children;
      this.fixDescendantHideNestedDescriptions(replacingOperatorWithRule.operator);
      replacingOperatorWithRule.operator.children.forEach(child => child.parent = replacingOperatorWithRule.operator);

      if (operator.parent === null) {
        rootOperator = replacingOperatorWithRule.operator;
      } else {
        replacingOperatorWithRule.operator.parent.children[replacingOperatorWithRule.operator.parent.children.indexOf(operator)]
          = replacingOperatorWithRule.operator;
      }
    }

    const ruleList = (
      replacingOperatorWithRule.operator.hasRule()
      && this.ruleList[replacingOperatorWithRule.operator.ruleIndex] !== replacingOperatorWithRule.rule
    )
      ? [
        ...this.ruleList.slice(0, replacingOperatorWithRule.operator.ruleIndex),
        replacingOperatorWithRule.rule,
        ...this.ruleList.slice(replacingOperatorWithRule.operator.ruleIndex + 1)
      ]
      : this.ruleList;

    return Object.assign(
      new EditorEligibilityTree(),
      this,
      {
        rootOperator,
        ruleList
      }
    );
  }

  immutableOperatorMoveUp(operatorId: string): EditorEligibilityTree {
    const rootOperatorCopy = this.rootOperator.createCompleteCopy();
    const operator = rootOperatorCopy.getOperatorById(operatorId);

    if (operator.parent === null) {
      return this;
    }

    const operatorParentIndex = operator.parent.children.indexOf(operator);

    if (operatorParentIndex === 0) {
      return this;
    }

    const siblingOperator = operator.parent.children[operatorParentIndex - 1];
    const firstHasRuleOperatorRuleIndex = operator.getFirstHasRuleOperator().ruleIndex;
    const lastHasRuleOperatorRuleIndex = operator.getLastHasRuleOperator().ruleIndex;
    const nodesCount = lastHasRuleOperatorRuleIndex - firstHasRuleOperatorRuleIndex + 1;
    const firstNullFunctionSiblingOperatorRuleIndex = siblingOperator.getFirstHasRuleOperator().ruleIndex;
    const lastNullFunctionSiblingOperatorRuleIndex = siblingOperator.getLastHasRuleOperator().ruleIndex;
    const siblingNodesCount = lastNullFunctionSiblingOperatorRuleIndex - firstNullFunctionSiblingOperatorRuleIndex + 1;

    operator.incrementRuleIndexFrom(0, -siblingNodesCount);
    siblingOperator.incrementRuleIndexFrom(0, +nodesCount);

    [operator.parent.children[operatorParentIndex - 1], operator.parent.children[operatorParentIndex]] =
      [operator.parent.children[operatorParentIndex], operator.parent.children[operatorParentIndex - 1]];

    return Object.assign(
      new EditorEligibilityTree(),
      this,
      {
        rootOperator: rootOperatorCopy,
        ruleList: [
          ...this.ruleList.slice(0, firstNullFunctionSiblingOperatorRuleIndex),
          ...this.ruleList.slice(firstHasRuleOperatorRuleIndex, lastHasRuleOperatorRuleIndex + 1),
          ...this.ruleList.slice(firstNullFunctionSiblingOperatorRuleIndex, lastNullFunctionSiblingOperatorRuleIndex + 1),
          ...this.ruleList.slice(lastHasRuleOperatorRuleIndex + 1)
        ]
      }
    );
  }

  immutableOperatorMoveDown(operatorId: string): EditorEligibilityTree {
    const rootOperatorCopy = this.rootOperator.createCompleteCopy();
    const operator = rootOperatorCopy.getOperatorById(operatorId);

    if (operator.parent === null) {
      return this;
    }

    const operatorParentIndex = operator.parent.children.indexOf(operator);

    if (operatorParentIndex === operator.parent.children.length - 1) {
      return this;
    }

    const siblingOperator = operator.parent.children[operatorParentIndex + 1];
    const firstHasRuleOperatorRuleIndex = operator.getFirstHasRuleOperator().ruleIndex;
    const lastHasRuleOperatorRuleIndex = operator.getLastHasRuleOperator().ruleIndex;
    const nodesCount = lastHasRuleOperatorRuleIndex - firstHasRuleOperatorRuleIndex + 1;
    const firstNullFunctionSiblingOperatorRuleIndex = siblingOperator.getFirstHasRuleOperator().ruleIndex;
    const lastNullFunctionSiblingOperatorRuleIndex = siblingOperator.getLastHasRuleOperator().ruleIndex;
    const siblingNodesCount = lastNullFunctionSiblingOperatorRuleIndex - firstNullFunctionSiblingOperatorRuleIndex + 1;

    operator.incrementRuleIndexFrom(0, +siblingNodesCount);
    siblingOperator.incrementRuleIndexFrom(0, -nodesCount);

    [operator.parent.children[operatorParentIndex + 1], operator.parent.children[operatorParentIndex]] =
      [operator.parent.children[operatorParentIndex], operator.parent.children[operatorParentIndex + 1]];

    return Object.assign(
      new EditorEligibilityTree(),
      this,
      {
        rootOperator: rootOperatorCopy,
        ruleList: [
          ...this.ruleList.slice(0, firstHasRuleOperatorRuleIndex),
          ...this.ruleList.slice(firstNullFunctionSiblingOperatorRuleIndex, lastNullFunctionSiblingOperatorRuleIndex + 1),
          ...this.ruleList.slice(firstHasRuleOperatorRuleIndex, lastHasRuleOperatorRuleIndex + 1),
          ...this.ruleList.slice(lastNullFunctionSiblingOperatorRuleIndex + 1)
        ]
      }
    );
  }

  immutableDuplicateBranch(operatorId: string): EditorEligibilityTree {
    let rootOperatorCopy = this.rootOperator.createCompleteCopy();
    let ruleList: EditorEligibilityRule[];

    const operator = rootOperatorCopy.getOperatorById(operatorId);
    const operatorCopy = operator.createCompleteClone();

    if (operator.parent === null) {
      const newParentOperator = EditorEligibilityOperator.createDefault();
      newParentOperator.function = 'and';
      operator.parent = newParentOperator;
      operatorCopy.parent = newParentOperator;
      newParentOperator.children = [operator, operatorCopy];

      if (operator.name !== null) {
        operatorCopy.name = `${operator.name} (duplicate)`;
      }

      operatorCopy.incrementRuleIndexFrom(0, this.ruleList.length);

      ruleList = [
        ...this.ruleList,
        ...this.ruleList.map(rule => rule === null ? null : rule.createClone())
      ];

      rootOperatorCopy = newParentOperator;
    } else {
      const firstHasRuleOperatorRuleIndex = operator.getFirstHasRuleOperator().ruleIndex;
      const lastHasRuleOperatorRuleIndex = operator.getLastHasRuleOperator().ruleIndex;
      const nodesCount = lastHasRuleOperatorRuleIndex - firstHasRuleOperatorRuleIndex + 1;

      operatorCopy.incrementRuleIndexFrom(0, nodesCount);
      rootOperatorCopy.incrementRuleIndexFrom(lastHasRuleOperatorRuleIndex + 1, nodesCount);

      operatorCopy.parent = operator.parent;
      const operatorParentIndex = operator.parent.children.indexOf(operator);
      operator.parent.children = [
        ...operator.parent.children.slice(0, operatorParentIndex + 1),
        operatorCopy,
        ...operator.parent.children.slice(operatorParentIndex + 1)
      ];

      ruleList = [
        ...this.ruleList.slice(0, lastHasRuleOperatorRuleIndex + 1),
        ...this.ruleList
          .slice(firstHasRuleOperatorRuleIndex, lastHasRuleOperatorRuleIndex + 1)
          .map(rule => rule === null ? null : rule.createClone()),
        ...this.ruleList.slice(lastHasRuleOperatorRuleIndex + 1),
      ];
    }

    return Object.assign(
      new EditorEligibilityTree(),
      this,
      {
        rootOperator: rootOperatorCopy,
        ruleList
      }
    );
  }

  immutableDeleteOperator(operatorId: string): EditorEligibilityTree {
    let rootOperatorCopy = this.rootOperator.createCompleteCopy();
    let ruleList: EditorEligibilityRule[];

    const operator = rootOperatorCopy.getOperatorById(operatorId);

    if (operator.parent === null) {
      rootOperatorCopy = null;
      ruleList = [];
    } else {
      const firstHasRuleOperatorRuleIndex = operator.getFirstHasRuleOperator().ruleIndex;
      const lastHasRuleOperatorRuleIndex = operator.getLastHasRuleOperator().ruleIndex;
      ruleList = [
        ...this.ruleList.slice(0, firstHasRuleOperatorRuleIndex),
        ...this.ruleList.slice(lastHasRuleOperatorRuleIndex + 1)
      ];
      rootOperatorCopy.incrementRuleIndexFrom(
        lastHasRuleOperatorRuleIndex + 1,
        -(lastHasRuleOperatorRuleIndex - firstHasRuleOperatorRuleIndex + 1)
      );

      if (operator.parent.children.length === 2) {
        const siblingOperator = operator.parent.children.find(child => child !== operator);

        if (siblingOperator.name === null) {
          siblingOperator.name = operator.parent.name;
        }

        if (operator.parent.parent === null) {
          rootOperatorCopy = siblingOperator;
        } else {
          const parentOperatorAsChildIndex = siblingOperator.parent.parent.children.indexOf(siblingOperator.parent);
          siblingOperator.parent.parent.children[parentOperatorAsChildIndex] = siblingOperator;
        }
        siblingOperator.parent = siblingOperator.parent.parent;
      } else {
        const operatorAsChildIndex = operator.parent.children.indexOf(operator);
        operator.parent.children.splice(operatorAsChildIndex, 1);
      }
    }

    return Object.assign(
      new EditorEligibilityTree(),
      this,
      {
        rootOperator: rootOperatorCopy,
        ruleList
      }
    );
  }

  fixDescendantHideNestedDescriptions(operator: EditorEligibilityOperator) {
    operator.children.forEach(child => {
      child.ancestorHideNestedDescriptions =
        operator.ancestorHideNestedDescriptions || operator.hideNestedDescriptions;
      this.fixDescendantHideNestedDescriptions(child);
    });
  }
}
